// Подсказката в „Запис на доставката", когато продукт от доставката е продаван
// без наличност (на минус). Не е забрана — казва какво ще стане при избраната
// дата: минусът от минали дни се отписва (доставката влиза цяла), минусът от
// същия ден се покрива (влиза остатъкът). Колко се покрива/отписва идва готово
// от BE (`GET /inventory/stock/negative` за датата) — тук е само текстът.
import dayjs from "dayjs";
import type { TFunction } from "i18next";
import type { NegativeStock, NegativeStockParams } from "../../../../api/inventoryNegativeStock";
import { formatQty } from "../../components/qty";

// Ред от записа: продукт · склад · количество в базовата мярка
export type IncomingLine = { productId: number; warehouseId: number; quantity: number };

export type NegativeHintProduct = { name: string; unit: string | null; decimals: number };

export type NegativeHint = { key: string; sentences: string[] };

const QUANTITY_EPSILON = 0.0005;
const shortDay = (isoDay: string) => dayjs(isoDay).format("DD.MM");

// Заявките — по една на склад, само с продуктите от доставката
export const negativeStockParamsOf = (
  lines: IncomingLine[],
  date: string,
  editMovementId: number | null,
): NegativeStockParams[] => {
  const byWarehouse = new Map<number, Set<number>>();
  for (const line of lines) {
    if (line.quantity <= 0) continue;
    const ids = byWarehouse.get(line.warehouseId) ?? new Set<number>();
    ids.add(line.productId);
    byWarehouse.set(line.warehouseId, ids);
  }
  return [...byWarehouse.entries()]
    .sort(([left], [right]) => left - right)
    .map(([warehouseId, ids]) => ({
      warehouseId,
      date,
      productIds: [...ids].sort((left, right) => left - right),
      excludeMovementId: editMovementId,
    }));
};

// Изреченията за един продукт на минус. Пример: „Кола: 10 бр продадени без
// наличност на 28.09. С дата 29.09 те се отписват и влизат всичките 20 бр. Ако
// стоката е дошла на 28.09 — сложи тази дата, тогава ще покрие продаденото."
export const negativeHintOf = (
  negative: NegativeStock,
  incoming: number,
  product: NegativeHintProduct,
  date: string,
  t: TFunction,
): string[] => {
  const unit = product.unit ? ` ${product.unit}` : "";
  const quantity = (value: number) => `${formatQty(value, product.decimals)}${unit}`;
  const days =
    negative.days.length === 1
      ? shortDay(negative.days[0].date)
      : negative.days.map((day) => `${shortDay(day.date)} (${quantity(day.quantity)})`).join(", ");
  const covered = Math.min(incoming, negative.coverableQuantity);
  const rest = Math.max(0, incoming - covered);
  const writtenOff = negative.writtenOffQuantity;
  const head = t("inventoryPage.saveNegHead", { name: product.name, qty: quantity(negative.negative), days });
  const chosenDay = shortDay(date);
  // най-ранният ден на минуса — доставка с тази дата покрива всичко
  const earliestDay = negative.days.length ? shortDay(negative.days[0].date) : chosenDay;
  if (writtenOff > QUANTITY_EPSILON && covered > QUANTITY_EPSILON)
    return [
      head,
      t("inventoryPage.saveNegMixed", {
        date: chosenDay,
        writtenOff: quantity(writtenOff),
        covered: quantity(covered),
        rest: quantity(rest),
      }),
      t("inventoryPage.saveNegMoveDate", { day: earliestDay }),
    ];
  if (writtenOff > QUANTITY_EPSILON)
    return [
      head,
      t("inventoryPage.saveNegWrittenOff", { date: chosenDay, incoming: quantity(incoming) }),
      t("inventoryPage.saveNegMoveDate", { day: earliestDay }),
    ];
  return [
    head,
    t("inventoryPage.saveNegCovered", { date: chosenDay, covered: quantity(covered), rest: quantity(rest) }),
  ];
};

// Подсказките за целия запис — по продукт и склад, в реда на редовете
export const negativeHintsOf = (
  lines: IncomingLine[],
  negativesByWarehouse: { warehouseId: number; items: NegativeStock[] }[],
  productOf: (productId: number) => NegativeHintProduct,
  date: string,
  t: TFunction,
): NegativeHint[] => {
  const negativeOf = new Map<string, NegativeStock>();
  for (const { warehouseId, items } of negativesByWarehouse)
    for (const item of items) negativeOf.set(`${warehouseId}:${item.productId}`, item);
  const incomingOf = new Map<string, number>();
  for (const line of lines) {
    const key = `${line.warehouseId}:${line.productId}`;
    incomingOf.set(key, (incomingOf.get(key) ?? 0) + line.quantity);
  }
  return [...incomingOf.entries()].flatMap(([key, incoming]) => {
    const negative = negativeOf.get(key);
    if (!negative || negative.negative <= QUANTITY_EPSILON || incoming <= 0) return [];
    const productId = Number(key.split(":")[1]);
    return [{ key, sentences: negativeHintOf(negative, incoming, productOf(productId), date, t) }];
  });
};
